import * as THREE from 'three'
import { viewport, onResize } from '../core/viewport.js'
import { params } from '../config/persistence.js'

export const initTrail = () => {
    const rtOptions = {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        stencilBuffer: false,
        depthBuffer: false,
        type: THREE.HalfFloatType,
    }

    const rtWidth = Math.floor(viewport.width * viewport.pixelRatio * params.trailResolutionScale)
    const rtHeight = Math.floor(viewport.height * viewport.pixelRatio * params.trailResolutionScale)
    let renderTargetA = new THREE.WebGLRenderTarget(rtWidth, rtHeight, rtOptions)
    let renderTargetB = new THREE.WebGLRenderTarget(rtWidth, rtHeight, rtOptions)

    const feedbackScene = new THREE.Scene()
    const feedbackCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
    const feedbackMaterial = new THREE.ShaderMaterial({
        uniforms: {
            currentFrame: { value: null },
            previousFrame: { value: null },
            trailAmount: { value: params.trailAmount / 100 },
            blurAmount: { value: params.trailBlurAmount },
            brightPersist: { value: params.trailBrightPersist },
            blendMode: { value: params.trailBlendMode },
            texelSize: { value: new THREE.Vector2(1.0 / rtWidth, 1.0 / rtHeight) },
            uDeltaTime: { value: 1.0 / 60.0 },
        },
        vertexShader: /*glsl*/`
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = vec4(position, 1.0);
            }
        `,
        fragmentShader: /*glsl*/`
            uniform sampler2D currentFrame;
            uniform sampler2D previousFrame;
            uniform float trailAmount;
            uniform float blurAmount;
            uniform float brightPersist;
            uniform int blendMode;
            uniform vec2 texelSize;
            uniform float uDeltaTime;
            varying vec2 vUv;

            vec3 sampleBlurred(sampler2D tex, vec2 uv) {
                vec3 sum = vec3(0.0);
                sum += texture2D(tex, uv + vec2(-1.0, -1.0) * texelSize).rgb * 0.0625;
                sum += texture2D(tex, uv + vec2( 0.0, -1.0) * texelSize).rgb * 0.125;
                sum += texture2D(tex, uv + vec2( 1.0, -1.0) * texelSize).rgb * 0.0625;
                sum += texture2D(tex, uv + vec2(-1.0,  0.0) * texelSize).rgb * 0.125;
                sum += texture2D(tex, uv).rgb * 0.25;
                sum += texture2D(tex, uv + vec2( 1.0,  0.0) * texelSize).rgb * 0.125;
                sum += texture2D(tex, uv + vec2(-1.0,  1.0) * texelSize).rgb * 0.0625;
                sum += texture2D(tex, uv + vec2( 0.0,  1.0) * texelSize).rgb * 0.125;
                sum += texture2D(tex, uv + vec2( 1.0,  1.0) * texelSize).rgb * 0.0625;
                return sum;
            }

            void main() {
                vec3 current = texture2D(currentFrame, vUv).rgb;
                vec3 prevSharp = texture2D(previousFrame, vUv).rgb;
                vec3 prevBlurred = sampleBlurred(previousFrame, vUv);
                vec3 previous = mix(prevSharp, prevBlurred, blurAmount);

                float lum = dot(previous, vec3(0.2126, 0.7152, 0.0722));
                float baseDecay = trailAmount + brightPersist * lum;
                baseDecay = clamp(baseDecay, 0.0, 0.995);
                float decay = pow(baseDecay, uDeltaTime * 60.0);

                vec3 result;
                if (blendMode == 0) {
                    result = max(current, previous * decay);
                } else if (blendMode == 1) {
                    vec3 faded = previous * decay;
                    result = current + faded - current * faded;
                } else {
                    result = mix(current, previous, decay);
                }

                gl_FragColor = vec4(result, 1.0);
            }
        `,
        depthTest: false,
        depthWrite: false,
    })
    feedbackScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), feedbackMaterial))

    const outputScene = new THREE.Scene()
    const outputCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
    const outputMaterial = new THREE.ShaderMaterial({
        uniforms: {
            currentFrame: { value: null },
            previousFrame: { value: null },
            trailAmount: { value: params.trailAmount / 100 },
            brightPersist: { value: params.trailBrightPersist },
            blendMode: { value: params.trailBlendMode },
            uDeltaTime: { value: 1.0 / 60.0 },
        },
        vertexShader: /*glsl*/`
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = vec4(position, 1.0);
            }
        `,
        fragmentShader: /*glsl*/`
            uniform sampler2D currentFrame;
            uniform sampler2D previousFrame;
            uniform float trailAmount;
            uniform float brightPersist;
            uniform int blendMode;
            uniform float uDeltaTime;
            varying vec2 vUv;
            void main() {
                vec3 current = texture2D(currentFrame, vUv).rgb;
                vec3 previous = texture2D(previousFrame, vUv).rgb;
                float lum = dot(previous, vec3(0.2126, 0.7152, 0.0722));
                float baseDecay = trailAmount + brightPersist * lum;
                baseDecay = clamp(baseDecay, 0.0, 0.995);
                float decay = pow(baseDecay, uDeltaTime * 60.0);
                vec3 result;
                if (blendMode == 0) {
                    result = max(current, previous * decay);
                } else if (blendMode == 1) {
                    vec3 faded = previous * decay;
                    result = current + faded - current * faded;
                } else {
                    result = mix(current, previous, decay);
                }
                gl_FragColor = vec4(result, 1.0);
            }
        `,
        depthTest: false,
        depthWrite: false,
    })
    outputScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), outputMaterial))

    onResize((vp) => {
        const tw = Math.floor(vp.width * vp.pixelRatio * params.trailResolutionScale)
        const th = Math.floor(vp.height * vp.pixelRatio * params.trailResolutionScale)
        renderTargetA.setSize(tw, th)
        renderTargetB.setSize(tw, th)
        feedbackMaterial.uniforms.texelSize.value.set(1.0 / tw, 1.0 / th)
    })

    // Scratch render target for direct scene render (no bloom path)
    let directRT = new THREE.WebGLRenderTarget(rtWidth, rtHeight, rtOptions)

    onResize((vp) => {
        const tw = Math.floor(vp.width * vp.pixelRatio)
        const th = Math.floor(vp.height * vp.pixelRatio)
        directRT.setSize(tw, th)
    })

    const renderTrail = (renderer, composer, useBloom) => {
        let currentFrameTexture

        if (useBloom) {
            composer.renderToScreen = false
            composer.render()
            currentFrameTexture = composer.readBuffer.texture
        } else {
            renderer.setRenderTarget(directRT)
            renderer.render(composer.passes[0].scene, composer.passes[0].camera)
            currentFrameTexture = directRT.texture
        }

        feedbackMaterial.uniforms.currentFrame.value = currentFrameTexture
        feedbackMaterial.uniforms.previousFrame.value = renderTargetB.texture

        renderer.setRenderTarget(renderTargetA)
        renderer.render(feedbackScene, feedbackCamera)

        const temp = renderTargetA
        renderTargetA = renderTargetB
        renderTargetB = temp

        outputMaterial.uniforms.currentFrame.value = currentFrameTexture
        outputMaterial.uniforms.previousFrame.value = renderTargetB.texture
        renderer.setRenderTarget(null)
        renderer.render(outputScene, outputCamera)
    }

    return { feedbackMaterial, outputMaterial, renderTargetA, renderTargetB, renderTrail }
}
